import { useState, useMemo } from 'react';
import { Search, Phone, Eye, Clock, ArrowUp, ArrowDown, RefreshCw, ChevronDown, ChevronUp, Filter, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { ConversationView } from '@/components/client/ConversationView';
import { useAuth } from '@/hooks/useAuth';
import { useTraces, useTrace, useTraceDashboardStats } from '@/hooks/useTraces';
import type { LangfuseTrace, TraceFilters } from '@/types/traces';

// Helper functions
const formatDuration = (latency: number): string => {
  return `${latency.toFixed(2)}s`;
};

const formatCost = (cost: number): string => {
  return `$${cost.toFixed(4)}`;
};




const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

export const ClientTracesView: React.FC = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState<Partial<TraceFilters>>({
    search: '',
    sessionId: '',
    userId: '',
    name: '',
    fromTimestamp: '',
    toTimestamp: '',
    tags: [],
    environment: [],
    version: '',
    release: '',
    // orderBy y sortOrder removidos - no soportados por el nuevo endpoint
    page: 1,
    limit: 20
  });
  
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedTrace, setSelectedTrace] = useState<LangfuseTrace | null>(null);
  const [showTraceDetail, setShowTraceDetail] = useState(false);

  // Get client ID from user context
  const clientId = user?.client_project_id;
  
  if (!clientId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500 mb-2">Error: No se pudo identificar el cliente</p>
          <p className="text-sm text-gray-600">Por favor, vuelva a iniciar sesión</p>
        </div>
      </div>
    );
  }

  // Helper function to format date for input fields
  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Fetch traces using the API
  const { data: tracesData, isLoading: tracesLoading, error: tracesError, refetch: refetchTraces, isRefetching } = useTraces(filters);
  
  // Fetch dashboard stats
  const { isLoading: statsLoading } = useTraceDashboardStats();

  // Fetch selected trace details
  const { data: traceDetailData, isLoading: traceDetailLoading } = useTrace(selectedTrace?.id || '');

  // Calculate metrics based on current page data
  const calculatedMetrics = useMemo(() => {
    if (!tracesData?.data) {
      return {
        totalTraces: 0,
        tracesToday: 0,
        avgLatency: 0,
        totalCost: 0
      };
    }

    const traces = tracesData.data;
    const today = new Date().toDateString();
    
    // Calculate traces today (from current page only)
    const tracesToday = traces.filter(trace => {
      const traceDate = new Date(trace.timestamp).toDateString();
      return traceDate === today;
    }).length;

    // Calculate averages from current page (handle optional fields)
    const avgLatency = traces.length > 0 
      ? traces.reduce((sum, trace) => sum + (trace.latency || 0), 0) / traces.length
      : 0;
    
    
    const totalCost = traces.reduce((sum, trace) => sum + (trace.totalCost || 0), 0);

    return {
      totalTraces: tracesData.meta?.totalItems || 0, // Total real desde la API
      tracesToday,
      avgLatency,
      totalCost
    };
  }, [tracesData]);

  const handleFilterChange = (key: string, value: any) => {
    if (key === 'page') {
      setFilters(prev => ({ ...prev, [key]: value }));
    } else {
      // For other filters, reset to first page
      setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    }
  };

  const handleViewTrace = (trace: LangfuseTrace) => {
    setSelectedTrace(trace);
    setShowTraceDetail(true);
  };

  const handleRefresh = async () => {
    await refetchTraces();
  };

  if (tracesError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500 mb-2">Error al cargar las llamadas</p>
          <p className="text-sm text-gray-600">{(tracesError as Error).message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">Mis Llamadas</h1>
          <p className="text-gray-600">Historial y análisis de llamadas</p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={isRefetching || tracesLoading}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
          {isRefetching ? 'Actualizando...' : 'Actualizar'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Llamadas</CardTitle>
            <Phone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : calculatedMetrics.totalTraces}
            </div>
            <p className="text-xs text-muted-foreground">
              {filters.fromTimestamp || filters.toTimestamp ? 'Período seleccionado' : 'Todos los tiempos'}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Llamadas Hoy</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : calculatedMetrics.tracesToday}
            </div>
            <p className="text-xs text-muted-foreground">
              Registradas hoy
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Duración Promedio</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : formatDuration(calculatedMetrics.avgLatency)}
            </div>
            <p className="text-xs text-muted-foreground">
              En esta página
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <CardTitle className="text-lg">Filtros</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center gap-1"
            >
              {showAdvancedFilters ? (
                <><ChevronUp className="h-4 w-4" /> Ocultar filtros</>
              ) : (
                <><ChevronDown className="h-4 w-4" /> Más filtros</>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Basic filters - always visible */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar llamadas..."
                className="pl-10"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            
            <Input
              placeholder="ID de Sesión"
              value={filters.sessionId || ''}
              onChange={(e) => handleFilterChange('sessionId', e.target.value)}
            />
            
            <Input
              type="date"
              placeholder="Desde"
              value={formatDateForInput(filters.fromTimestamp || '')}
              onChange={(e) => handleFilterChange('fromTimestamp', e.target.value)}
            />
            
            <Input
              type="date"
              placeholder="Hasta"
              value={formatDateForInput(filters.toTimestamp || '')}
              onChange={(e) => handleFilterChange('toTimestamp', e.target.value)}
            />
          </div>
          
          {/* Advanced filters - collapsible */}
          {showAdvancedFilters && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                <Input
                  placeholder="ID de Usuario"
                  value={filters.userId || ''}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                />
                
                <Input
                  placeholder="Nombre de llamada"
                  value={filters.name || ''}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                />
                
                <Input
                  placeholder="Versión"
                  value={filters.version || ''}
                  onChange={(e) => handleFilterChange('version', e.target.value)}
                />
                
                <Input
                  placeholder="Release"
                  value={filters.release || ''}
                  onChange={(e) => handleFilterChange('release', e.target.value)}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <Select 
                  value={filters.orderBy || 'timestamp'}
                  onValueChange={(value: any) => handleFilterChange('orderBy', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="timestamp">Fecha</SelectItem>
                    <SelectItem value="latency">Duración</SelectItem>
                    <SelectItem value="totalCost">Costo</SelectItem>
                    <SelectItem value="name">Nombre</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleFilterChange('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="flex items-center gap-2"
                >
                  {filters.sortOrder === 'asc' ? (
                    <><ArrowUp className="h-4 w-4" /> Ascendente</>
                  ) : (
                    <><ArrowDown className="h-4 w-4" /> Descendente</>
                  )}
                </Button>
                
                <div />
              </div>
            </>
          )}
          
          {/* Control buttons */}
          <div className="flex justify-between items-center mt-3 pt-3 border-t">
            <Select 
              value={filters.limit?.toString() || '20'}
              onValueChange={(value) => handleFilterChange('limit', parseInt(value))}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 por pág.</SelectItem>
                <SelectItem value="20">20 por pág.</SelectItem>
                <SelectItem value="50">50 por pág.</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilters({
                  search: '',
                  sessionId: '',
                  userId: '',
                  name: '',
                  fromTimestamp: '',
                  toTimestamp: '',
                  tags: [],
                  environment: [],
                  version: '',
                  release: '',
                  orderBy: 'timestamp',
                  sortOrder: 'desc',
                  page: 1,
                  limit: 20
                });
                setShowAdvancedFilters(false);
              }}
            >
              Limpiar filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Traces Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Llamadas</CardTitle>
          <CardDescription>
            {tracesData ? `Total: ${tracesData.meta?.totalItems || 0} llamadas` : 'Cargando...'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tracesLoading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Fecha/Hora</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Sesión</TableHead>
                    <TableHead>Duración</TableHead>
                    {user?.role !== 'client' && <TableHead>Costo</TableHead>}
                    <TableHead>Ambiente</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(tracesData?.data || []).map((trace) => (
                    <TableRow key={trace.id}>
                      <TableCell className="font-mono text-sm">
                        {trace.id.slice(0, 8)}...
                      </TableCell>
                      <TableCell className="font-medium">
                        {trace.name || 'Sin nombre'}
                      </TableCell>
                      <TableCell>
                        {formatDateTime(trace.timestamp)}
                      </TableCell>
                      <TableCell>
                        {trace.userId ? (
                          <span className="font-mono text-sm">{trace.userId.slice(0, 8)}...</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {trace.sessionId ? (
                          <span className="font-mono text-sm">{trace.sessionId.slice(0, 8)}...</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {trace.latency != null ? (
                          <Badge variant={trace.latency > 5000 ? 'destructive' : trace.latency > 2000 ? 'secondary' : 'default'}>
                            {formatDuration(trace.latency)}
                          </Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
{user?.role !== 'client' && (
                        <TableCell>
                          {trace.totalCost != null ? formatCost(trace.totalCost) : <span className="text-gray-400">$0.0000</span>}
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge variant="outline">
                          {trace.environment || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewTrace(trace)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {tracesData && (
                <div className="flex justify-between items-center mt-4">
                  <span className="text-sm text-gray-600">
                    Página {tracesData.meta?.page || 1} de {tracesData.meta?.totalPages || 1} ({tracesData.meta?.totalItems || 0} total)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleFilterChange('page', (filters.page || 1) - 1)}
                      disabled={(filters.page || 1) <= 1}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleFilterChange('page', (filters.page || 1) + 1)}
                      disabled={!tracesData.meta?.totalPages || (filters.page || 1) >= tracesData.meta.totalPages}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Trace Detail Dialog */}
      <Dialog open={showTraceDetail} onOpenChange={setShowTraceDetail}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Detalle de Llamada: {selectedTrace?.name || selectedTrace?.id}
            </DialogTitle>
            <DialogDescription>
              Información completa de la llamada
            </DialogDescription>
          </DialogHeader>
          
          {traceDetailLoading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner />
            </div>
          ) : traceDetailData?.data ? (
            <Tabs defaultValue="conversation" className="mt-4">
              <TabsList className={`grid w-full ${user?.role === 'client' ? 'grid-cols-2' : 'grid-cols-4'}`}>
                <TabsTrigger value="conversation">Conversación</TabsTrigger>
                <TabsTrigger value="info">Información</TabsTrigger>
                {user?.role !== 'client' && <TabsTrigger value="io">Input/Output</TabsTrigger>}
                {user?.role !== 'client' && <TabsTrigger value="metadata">Metadata</TabsTrigger>}
              </TabsList>
              
              <TabsContent value="conversation" className="max-h-[500px] overflow-y-auto">
                <ConversationView trace={traceDetailData.data} />
              </TabsContent>
              
              <TabsContent value="info" className="space-y-4 max-h-[400px] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">ID</p>
                    <p className="font-mono text-sm">{traceDetailData.data.id}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Nombre</p>
                    <p>{traceDetailData.data.name || 'Sin nombre'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Asistente</p>
                    <p>{traceDetailData.data.metadata?.assistant_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Timestamp</p>
                    <p>{formatDateTime(traceDetailData.data.timestamp)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Ambiente</p>
                    <Badge>{traceDetailData.data.environment}</Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Usuario ID</p>
                    <p className="font-mono text-sm">{traceDetailData.data.userId || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Sesión ID</p>
                    <p className="font-mono text-sm">{traceDetailData.data.sessionId || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Duración</p>
                    <p>{traceDetailData.data.latency != null ? formatDuration(traceDetailData.data.latency) : 'N/A'}</p>
                  </div>
{user?.role !== 'client' && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Costo</p>
                      <p>{traceDetailData.data.totalCost != null ? formatCost(traceDetailData.data.totalCost) : 'N/A'}</p>
                    </div>
                  )}
                </div>
                
                {traceDetailData.data.tags && traceDetailData.data.tags.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Tags</p>
                    <div className="flex flex-wrap gap-1">
                      {traceDetailData.data.tags.map(tag => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
              
              {user?.role !== 'client' && (
                <TabsContent value="io" className="space-y-4 max-h-[400px] overflow-y-auto">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Input</h3>
                      <pre className="bg-gray-50 p-4 rounded overflow-auto text-sm border">
                        {JSON.stringify(traceDetailData.data.input, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Output</h3>
                      <pre className="bg-gray-50 p-4 rounded overflow-auto text-sm border">
                        {JSON.stringify(traceDetailData.data.output, null, 2)}
                      </pre>
                    </div>
                  </div>
                </TabsContent>
              )}
              
              {user?.role !== 'client' && (
                <TabsContent value="metadata" className="space-y-4 max-h-[400px] overflow-y-auto">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Metadata</h3>
                    <pre className="bg-gray-50 p-4 rounded text-sm border max-h-[150px] overflow-auto whitespace-pre-wrap break-words">
                      {JSON.stringify(traceDetailData.data.metadata, null, 2)}
                    </pre>
                  </div>
                  
                  {traceDetailData.data.scores && traceDetailData.data.scores.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Scores</h3>
                      <pre className="bg-gray-50 p-4 rounded text-sm border max-h-[150px] overflow-auto whitespace-pre-wrap break-words">
                        {JSON.stringify(traceDetailData.data.scores, null, 2)}
                      </pre>
                    </div>
                  )}
                </TabsContent>
              )}
            </Tabs>
          ) : (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <p className="text-red-500 mb-2">Error al cargar los detalles</p>
                <p className="text-sm text-gray-600">No se pudo obtener la información de la llamada</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};